import { useEffect, useRef, useState } from 'react';

import { useLocation, useNavigate } from '@xing-com/crate-core-router';
import { XING_JOBS_SEARCH_PATH } from '@xing-com/crate-core-routes';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { useSearchParameters } from '@xing-com/crate-hooks-use-search-parameters';
import {
  SEARCH_STATE_FILTER_PARAMS,
  SEARCH_STATE_PARAMS,
  SEARCH_QUERY_ID_PARAM,
  SEARCH_STATE_DISABLED_FILTER_PARAMS,
  SEARCH_STATE_DISABLED_SUFFIX,
  AI_FABI_TRACKING_TOKEN_PARAM,
} from '@xing-com/crate-jobs-constants';
import { useSerpsMatch } from '@xing-com/crate-jobs-domain-serps-hooks';

import { useFilter, useFilterSet } from '../query-params';
import { useIsAiSearch } from '../use-is-ai-search';
import { useSearchLocation } from '../use-search-location';
import { isFullRemoteOnlyActive } from '../use-search-state';

/**
 * Optional values that you can pass to the onSubmit function that will
 * override the actual values
 */
export type ForcedSubmitValues = {
  [SEARCH_STATE_PARAMS.keywords]?: string;
  [AI_FABI_TRACKING_TOKEN_PARAM]?: string;
};

const getFilteredSearchParams = (
  params: Record<string, string>
): URLSearchParams => {
  const searchParams = new URLSearchParams(params);

  Object.entries(params).forEach(([name, value]) => {
    if (!value) {
      searchParams.delete(name);
    }
  });

  return searchParams;
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const useSearchBar = () => {
  const isAiSearch = useIsAiSearch();
  const navigate = useNavigate();
  const routerLocation = useLocation();

  const { setSearchParameters } = useSearchParameters();

  const { isLoggedOut } = useLoginState();
  const serps = useSerpsMatch();
  const isSerps = serps && isLoggedOut;

  const { value: keywordsParam } = useFilter(SEARCH_STATE_PARAMS.keywords);
  const { value: locationParam } = useFilter(SEARCH_STATE_PARAMS.location);
  const { value: cityIdParam } = useFilter(SEARCH_STATE_PARAMS.cityId);
  const { value: remoteOptionParam } = useFilterSet(
    SEARCH_STATE_PARAMS.remoteOption
  );

  // SERPs-related params
  const { value: disciplineParam } = useFilter(SEARCH_STATE_PARAMS.discipline);
  const { value: employmentTypeParam } = useFilter(
    SEARCH_STATE_PARAMS.employmentType
  );
  const { value: searchQueryIdParam } = useFilter(SEARCH_QUERY_ID_PARAM);

  const keywordsValue = keywordsParam ?? '';
  const [keywords, setKeywords] = useState(keywordsValue);
  const shouldResetFilters = useRef(false);

  const disableResetFilters = (): void => {
    shouldResetFilters.current = false;
  };

  // Check if the URL params have been changed (i.e. navigating back with the browser)
  useEffect(() => {
    if (keywordsValue !== keywords) {
      setKeywords(keywordsValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keywordsValue]);

  const { location, cityId, onChangeLocation, onSelectLocation } =
    useSearchLocation({
      initialLocation: locationParam,
      initialCityId: cityIdParam,
      onChange: disableResetFilters,
    });

  // on every location change, we reset the query dirtiness
  useEffect(() => {
    disableResetFilters();
  }, [routerLocation]);

  const handleOnChangeKeywords = (value: string): void => {
    shouldResetFilters.current = true;
    setKeywords(value);
  };

  const handleOnSubmit = (
    force: ForcedSubmitValues = {},
    replace = false
  ): void => {
    const shouldResetAllFilters =
      isAiSearch &&
      (shouldResetFilters.current ||
        routerLocation.search.includes(SEARCH_STATE_DISABLED_SUFFIX));

    const params = {
      [SEARCH_STATE_PARAMS.keywords]: force.keywords ?? keywords,
      [SEARCH_STATE_PARAMS.location]: location,
      [SEARCH_STATE_PARAMS.cityId]: cityId,
      // If we are in an AI search and we change the query, we need to reset all the filters
      ...(shouldResetAllFilters && {
        ...Object.fromEntries(
          Object.values(SEARCH_STATE_FILTER_PARAMS).map((param) => [param, ''])
        ),
        ...Object.fromEntries(
          Object.values(SEARCH_STATE_DISABLED_FILTER_PARAMS).map((param) => [
            param,
            '',
          ])
        ),
      }),
      ...Object(
        force[AI_FABI_TRACKING_TOKEN_PARAM] && {
          [AI_FABI_TRACKING_TOKEN_PARAM]: force[AI_FABI_TRACKING_TOKEN_PARAM],
        }
      ),
    };

    setSearchParameters(params, { removeEmpty: true, replace });

    // if it's a SERP page and "submit" was clicked, redirect to the search page
    if (isSerps) {
      const serpsParams = {
        ...params,
        ...(disciplineParam && {
          [SEARCH_STATE_PARAMS.discipline]: disciplineParam,
        }),
        ...(employmentTypeParam && {
          [SEARCH_STATE_PARAMS.employmentType]: employmentTypeParam,
        }),
        ...(searchQueryIdParam && {
          [SEARCH_QUERY_ID_PARAM]: searchQueryIdParam,
        }),
      };

      navigate(
        `${XING_JOBS_SEARCH_PATH}?${getFilteredSearchParams(serpsParams).toString()}`,
        { replace: true }
      );
    }
  };

  return {
    keywords,
    location,
    isLocationDisabled: isFullRemoteOnlyActive(remoteOptionParam),
    onChangeKeywords: handleOnChangeKeywords,
    onChangeLocation,
    onSelectLocation,
    onSubmit: handleOnSubmit,
  };
};
